import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  AlertOctagon,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  FileText,
  Inbox,
  RotateCcw,
  Search,
  XOctagon,
} from "lucide-react";
import { toast } from "react-toastify";
import { containerOrderIntakeAPI } from "../../services/api";
import Button from "../../components/UI/Button";
import LoadingSpinner from "../../components/UI/LoadingSpinner";
import { useSocketEvent } from "../../hooks/useSocketEvent";
import { INTAKE_CHANGED_EVENT, useIntakeCounts } from "../../hooks/useIntakeCounts";
import IntakeConnectionBanner from "../../components/containerOrderIntake/IntakeConnectionBanner";
import HandlerAssign from "../../components/handler/HandlerAssign";
import HandlerScopeFilter from "../../components/handler/HandlerScopeFilter";
import { useHandlerScope } from "../../hooks/useHandlerScope";
import type {
  ContainerOrderIntakeListItem,
  IntakeCounts,
  IntakeSort,
  IntakeSortKey,
  IntakeStatus,
} from "../../types/containerOrderIntake";

const TABS: IntakeStatus[] = ["pending", "accepted", "declined", "failed"];

const PAGE_SIZE = 20;

/** How a tab reads until someone picks a column: the queue oldest first, the records newest first. */
const DEFAULT_SORT: Record<IntakeStatus, IntakeSort> = {
  pending: { key: "received", order: "asc" },
  accepted: { key: "decided", order: "desc" },
  declined: { key: "decided", order: "desc" },
  failed: { key: "failed", order: "desc" },
};

/** What the last column sorts by on each tab — its decision, its failure; the review column does not sort. */
const LAST_COLUMN_SORT: Record<IntakeStatus, IntakeSortKey | null> = {
  pending: null,
  accepted: "decided",
  declined: "decided",
  failed: "failed",
};

/**
 * What share of the table each column takes.
 *
 * Given no widths, the browser hands every column an equal share of the slack
 * — which is why a date and a booking number ended up a hundred points apart
 * with nothing between them. These shares are deliberate instead: the short
 * identifiers get what they need, and the slack goes where there is something
 * to put in it. The last column takes whatever is left (a third of the table
 * on the decided tabs), which is the one carrying a failure message and its
 * Retry button.
 */
const COL = {
  received: "w-[13%]",
  document: "w-[14%]",
  client: "w-[16%]",
  booking: "w-[13%]",
  date: "w-[12%]",
  containers: "w-[9%]",
  // Slim: it holds one bubble. The heading is what sets the floor.
  handler: "w-[8%]",
};

const CELL = "px-4 py-2.5 whitespace-nowrap align-top";
const HEAD = "px-4 py-2.5 text-left font-semibold";
/** The last column: no share of its own, so it takes the rest. It may wrap. */
const WIDE = "px-4 py-2.5 align-top";

const fmtDateTime = (d?: string | null) =>
  d ? new Date(d).toLocaleString("et-EE", { dateStyle: "short", timeStyle: "short" }) : "—";

const fmtDay = (d?: string | null) =>
  d ? new Date(`${d}T00:00:00Z`).toLocaleDateString("et-EE", { timeZone: "UTC" }) : "—";

const personName = (p?: { firstName?: string; lastName?: string; email?: string } | null) =>
  p ? [p.firstName, p.lastName].filter(Boolean).join(" ") || p.email || "" : "";

/**
 * Order intake — every transport order Oliver has read, by review state.
 *
 * Pending opens oldest first: it is a queue, and the order that has waited
 * longest is the one to deal with next. The other tabs are records and open
 * newest first. Any column header sorts the tab by it instead — click again
 * to reverse — and the server sorts, so the order holds across pages.
 */
const ContainerOrderIntakeList: React.FC = () => {
  const { t } = useTranslation("containerOrderIntake");
  const navigate = useNavigate();

  const [status, setStatus] = useState<IntakeStatus>("pending");
  const [items, setItems] = useState<ContainerOrderIntakeListItem[]>([]);
  const [counts, setCounts] = useState<IntakeCounts>({
    pending: 0,
    accepted: 0,
    declined: 0,
    failed: 0,
  });
  const [retrying, setRetrying] = useState<string | null>(null);
  // Null unless this reader is an admin — the server withholds it otherwise.
  const { connection } = useIntakeCounts();
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<IntakeSort>(DEFAULT_SORT.pending);
  const [loading, setLoading] = useState(true);

  /**
   * Whose queue to show — remembered for this screen on its own.
   *
   * Its own memory rather than one shared with the orders list, because they
   * are two jobs: reviewing the morning's intake is usually done from
   * *Unassigned* (what nobody has picked up), while planning the week is done
   * from Mine. One setting carried between them would have each screen opening
   * on a filter set for the other.
   */
  const { scope: handlerScope, setScope: setHandlerScope, param: handlerParam } =
    useHandlerScope("intake");
  // Where the rows start, so the loading spinner sits over them, not the headings.
  const headRef = useRef<HTMLTableSectionElement>(null);

  const load = useCallback(async () => {
    try {
      const res = await containerOrderIntakeAPI.list({
        status,
        page,
        limit: PAGE_SIZE,
        search: query || undefined,
        sort: sort.key,
        order: sort.order,
        handler: handlerParam,
      });
      const data = res.data.data;
      setItems(data.items);
      setCounts(data.counts);
      setPages(data.pagination.pages);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t("list.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [status, page, query, sort, handlerParam, t]);

  /**
   * Read a failed order again.
   *
   * Oliver keeps the order after it leaves its queue, so this can still fetch
   * it however long ago the import gave up. On success the row moves to To
   * review by itself — the server says so over the socket.
   */
  const retry = async (intakeId: string) => {
    setRetrying(intakeId);
    try {
      const res = await containerOrderIntakeAPI.retry(intakeId);
      toast.success(res.data.message);
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t("list.retryFailed"));
      await load();
    } finally {
      setRetrying(null);
    }
  };

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  // Debounced search.
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setQuery(search.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useSocketEvent(INTAKE_CHANGED_EVENT, () => load());

  /**
   * Oliver sends no `originalName` today, so the column would be a header over
   * a row of dashes. Decided per page rather than switched off: once orders
   * start arriving with the document they were read from, the column comes
   * back on its own.
   */
  const showDocument = items.some((item) => item.sourceFileName);

  const lastSort = LAST_COLUMN_SORT[status];

  const sortBy = (key: IntakeSortKey) => {
    setSort((current) =>
      current.key === key
        ? { key, order: current.order === "asc" ? "desc" : "asc" }
        : { key, order: "asc" },
    );
    setPage(1);
  };

  const sortHeader = (key: IntakeSortKey, label: string, className: string) => {
    const active = sort.key === key;
    const Icon = !active ? ArrowUpDown : sort.order === "asc" ? ArrowUp : ArrowDown;
    return (
      <th
        aria-sort={active ? (sort.order === "asc" ? "ascending" : "descending") : "none"}
        className={className}
      >
        <button
          type="button"
          onClick={() => sortBy(key)}
          className={`inline-flex items-center gap-1 uppercase tracking-wide transition-colors hover:text-[#0B0F0E] ${
            active ? "text-[#0B0F0E]" : ""
          }`}
        >
          {label}
          <Icon className={`h-3 w-3 ${active ? "" : "opacity-40"}`} />
        </button>
      </th>
    );
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Inbox className="h-6 w-6 text-[#C97A3C]" />
          <div>
            <h1 className="text-xl font-medium text-[#0B0F0E]">{t("list.title")}</h1>
            <p className="text-[13px] text-[#8A9590]">{t("list.subtitle")}</p>
          </div>
        </div>
      </div>

      <IntakeConnectionBanner connection={connection} />

      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#E5E3DC]">
        <div className="flex gap-1">
          {TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => {
                // Another tab's rows would sit under this tab's headings until it loads.
                if (tab !== status) setItems([]);
                setStatus(tab);
                setSort(DEFAULT_SORT[tab]);
                setPage(1);
              }}
              className={`px-3 py-2 text-[13px] border-b-2 -mb-px transition-colors ${
                status === tab
                  ? "border-[#C97A3C] text-[#0B0F0E] font-medium"
                  : "border-transparent text-[#8A9590] hover:text-[#0B0F0E]"
              }`}
            >
              {t(`status.${tab}`)}
              <span
                className={`ml-2 rounded-full px-1.5 text-[11px] ${
                  tab === "pending" && counts.pending
                    ? "bg-[#C97A3C] text-white"
                    : tab === "failed" && counts.failed
                      ? "bg-red-600 text-white"
                      : "bg-[#F5F4F0] text-[#8A9590]"
                }`}
              >
                {counts[tab]}
              </span>
            </button>
          ))}
        </div>
        <div className="mb-2 flex items-center gap-2">
          {/* Beside the search rather than in a panel: on this screen the
              filter is used constantly — the queue is shared and the first
              question of the morning is what nobody has claimed. */}
          <HandlerScopeFilter
            scope={handlerScope}
            onChange={(next) => {
              setHandlerScope(next);
              setPage(1);
            }}
          />
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-[#8A9590]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("list.search")}
            className="w-64 border border-[#E5E3DC] rounded-md pl-8 pr-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#C97A3C]"
          />
        </div>
        </div>
      </div>

      {/* The headings stay put while a page loads — only the rows wait. */}
      {!loading && items.length === 0 ? (
        <div className="text-center py-16 text-[13px] text-[#8A9590]">
          {t(`list.empty.${status}`)}
        </div>
      ) : (
        <div className="relative overflow-x-auto bg-white border border-[#E5E3DC] rounded-lg">
          <table className="min-w-full text-[13px]">
            <thead
              ref={headRef}
              className="bg-[#F5F4F0] text-[11px] uppercase tracking-wide text-[#8A9590]"
            >
              <tr>
                {sortHeader("received", t("list.col.received"), `${HEAD} ${COL.received}`)}
                {showDocument &&
                  sortHeader("document", t("list.col.document"), `${HEAD} ${COL.document}`)}
                {sortHeader("client", t("list.col.client"), `${HEAD} ${COL.client}`)}
                {sortHeader("booking", t("list.col.booking"), `${HEAD} ${COL.booking}`)}
                {sortHeader("date", t("list.col.date"), `${HEAD} ${COL.date}`)}
                <th className={`${HEAD} ${COL.containers} text-right`}>
                  {t("list.col.containers")}
                </th>
                <th className={`${HEAD} ${COL.handler}`}>
                  {t("list.col.handler")}
                </th>
                {lastSort ? (
                  sortHeader(
                    lastSort,
                    status === "failed" ? t("list.col.problem") : t("list.col.decision"),
                    `${WIDE} text-left font-semibold`,
                  )
                ) : (
                  <th className={`${WIDE} text-left font-semibold`}>{t("list.col.issues")}</th>
                )}
              </tr>
            </thead>
            <tbody
              className={`transition-opacity ${loading && items.length ? "opacity-50 pointer-events-none" : ""}`}
            >
              {items.length === 0 && (
                <tr>
                  <td colSpan={showDocument ? 8 : 7} className="py-16">
                    <div className="flex justify-center">
                      <LoadingSpinner size="md" />
                    </div>
                  </td>
                </tr>
              )}
              {items.map((item) => (
                <tr
                  key={item._id}
                  onClick={
                    item.status === "failed"
                      ? undefined
                      : () => navigate(`/order-intake/${item._id}`)
                  }
                  className={`border-t border-[#EFEDE7] ${
                    item.status === "failed" ? "" : "hover:bg-[#FBFAF7] cursor-pointer"
                  }`}
                >
                  <td className={CELL}>
                    {fmtDateTime(item.receivedAt || item.createdAt)}
                    <div className="text-[11px] text-[#8A9590]">#{item.externalId}</div>
                  </td>
                  {showDocument && (
                    <td className={CELL}>
                      <span className="inline-flex items-center gap-1.5">
                        <FileText className="h-3.5 w-3.5 text-[#8A9590]" />
                        {item.sourceFileName || "—"}
                      </span>
                    </td>
                  )}
                  <td className={`${CELL} font-medium text-[#0B0F0E]`}>
                    {item.summary?.clientName || "—"}
                  </td>
                  <td className={CELL}>{item.summary?.bookingNumber || "—"}</td>
                  <td className={CELL}>{fmtDay(item.summary?.firstDate)}</td>
                  <td className={`${CELL} text-right`}>{item.summary?.containerCount ?? 0}</td>
                  {/* ── Who has taken this order for review ──────────────────
                      The single most-pressed control on the screen: one click
                      on an empty bubble claims the order, which is how twelve
                      dispatchers sharing one queue stop reviewing the same
                      order twice. It survives Accept — the created container
                      order's handler is seeded from it — so claiming here means
                      still owning the booking afterwards.

                      `stopPropagation` because the row itself opens the review
                      page. Without it, claiming an order would also navigate
                      into it, which is precisely what claiming from the list
                      exists to avoid having to do. */}
                  <td
                    className={CELL}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <HandlerAssign
                      handler={item.handlerId ?? null}
                      size="sm"
                      label={t("list.handlerFor", {
                        booking: item.summary?.bookingNumber || item.externalId,
                      })}
                      onAssign={async (handlerId) => {
                        const res = await containerOrderIntakeAPI.setHandler(
                          item._id,
                          handlerId,
                        );
                        // Under a scope the row may have just left this view —
                        // and the tab badges are scoped too, so both have to be
                        // re-read rather than patched.
                        load();
                        return res;
                      }}
                      onError={(message) => toast.error(message)}
                    />
                  </td>
                  <td className={WIDE}>
                    {status === "pending" && (
                      <span className="inline-flex items-center gap-3">
                        {item.summary.blockingCount > 0 ? (
                          <span className="inline-flex items-center gap-1 text-red-700">
                            <AlertOctagon className="h-3.5 w-3.5" />
                            {t("list.toFix", { count: item.summary.blockingCount })}
                          </span>
                        ) : (
                          <span className="text-green-700">{t("list.ready")}</span>
                        )}
                        {item.summary.warningCount > 0 && (
                          <span className="inline-flex items-center gap-1 text-amber-700">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            {item.summary.warningCount}
                          </span>
                        )}
                      </span>
                    )}
                    {status === "accepted" && (
                      <span>
                        {item.containerOrderId?.orderNumber && (
                          <span className="font-medium text-[#0B0F0E] mr-2">
                            {item.containerOrderId.orderNumber}
                          </span>
                        )}
                        <span className="text-[#8A9590]">
                          {personName(item.decidedBy)} · {fmtDateTime(item.decidedAt)}
                        </span>
                      </span>
                    )}
                    {status === "declined" && (
                      <span>
                        {item.declineReason}
                        <span className="text-[#8A9590]">
                          {" "}
                          · {personName(item.decidedBy)} · {fmtDateTime(item.decidedAt)}
                        </span>
                      </span>
                    )}
                    {status === "failed" && (
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-red-700">
                          <span className="inline-flex items-center gap-1 font-medium">
                            <XOctagon className="h-3.5 w-3.5" />
                            {item.failure?.message || t("list.unknownProblem")}
                          </span>
                          <span className="block text-[11px] text-[#8A9590]">
                            {t("list.attempts", { count: item.attempts ?? 0 })} ·{" "}
                            {fmtDateTime(item.failure?.at)}
                          </span>
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          icon={<RotateCcw className="h-4 w-4" />}
                          isLoading={retrying === item._id}
                          disabled={!!retrying}
                          onClick={() => retry(item._id)}
                        >
                          {t("list.retry")}
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {loading && items.length > 0 && (
            <div
              className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-center"
              style={{ top: headRef.current?.offsetHeight ?? 0 }}
            >
              <LoadingSpinner size="md" />
            </div>
          )}
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 text-[13px]">
          <Button
            variant="outline"
            size="sm"
            icon={<ChevronLeft className="h-4 w-4" />}
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          />
          <span>
            {page} / {pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            icon={<ChevronRight className="h-4 w-4" />}
            disabled={page >= pages}
            onClick={() => setPage((p) => p + 1)}
          />
        </div>
      )}
    </div>
  );
};

export default ContainerOrderIntakeList;
